/**
 * event catalogue: every event the app may send, as plain data
 * - names are snake_case `object_action`, identical in every tool
 * - `required`: property keys the event must carry (warned on in dev)
 * - `destinations`: which tools receive it (the per-event volume switch)
 * - `gtmEvent`: GA4 recommended name to push to dataLayer, when one exists
 * - `owner`: "client" | "server" — which side emits it
 *
 * e.g.
 *   signup_completed: {
 *     required: ["method", "signup_path"],
 *     destinations: ["mixpanel", "gtm", "hotjar", "sentry"],
 *     gtmEvent: "sign_up",
 *     owner: "client"
 *   }
 */

export const SIGNUP_REASONS = {
  ADD_TO_PLAYLIST: "add_to_playlist",
  DOWNLOAD_LIMIT: "download_limit",
  FAVORITE: "favorite",
  FORM_CROSS_LINK: "form_cross_link",
  GATED_CONTENT: "gated_content",
  INCOMPLETE_REGISTRATION: "incomplete_registration",
  MIXDOWN_LIMIT: "mixdown_limit",
  NAV_CTA: "nav_cta",
  OPEN_EDITOR: "open_editor",
  POST_ORDER: "post_order",
  POST_SUBSCRIPTION: "post_subscription",
  PROTECTED_ROUTE: "protected_route"
};

export const SIGNUP_SURFACES = {
  ACCESS_MESSAGE: "access_message",
  CHECKOUT: "checkout",
  DOWNLOAD_MODAL: "download_modal",
  EDITOR: "editor",
  EDITOR_POPOVER: "editor_popover",
  MENU: "menu",
  MOBILE_DRAWER: "mobile_drawer",
  ORDER_CONFIRMATION: "order_confirmation",
  PAYMENT_RETURN: "payment_return",
  PLAYER: "player",
  RESET: "reset",
  ROUTE_GUARD: "route_guard",
  SIGNIN_FORM: "signin_form",
  TRACK_ROW: "track_row"
};

// populated with the events we want to track
export const CATALOGUE = {
  // fired on each real navigation
  page_viewed: {
    required: ["route", "path"],
    destinations: ["mixpanel"],
    owner: "client"
  },

  signup_prompted: {
    required: ["reason", "surface"],
    destinations: ["mixpanel", "hotjar", "sentry"],
    owner: "client"
  },

  signup_form_viewed: {
    required: ["prefilled_email", "subscription_path"],
    destinations: ["mixpanel", "hotjar", "sentry"],
    owner: "client"
  },
  signup_submitted: {
    required: ["method"],
    destinations: ["mixpanel", "hotjar", "sentry"],
    owner: "client"
  },
  signup_completed: {
    required: ["method", "signup_path"],
    destinations: ["mixpanel", "hotjar", "sentry"],
    owner: "client"
  },
  signup_failed: {
    required: ["method", "reason"],
    destinations: ["mixpanel", "hotjar", "sentry"],
    owner: "client"
  },

  signin_completed: {
    required: ["method"],
    destinations: ["mixpanel", "hotjar", "sentry"],
    owner: "client"
  },
  signin_failed: {
    required: ["reason"],
    destinations: ["mixpanel", "hotjar", "sentry"],
    owner: "client"
  },

  password_reset_requested: {
    required: ["result"],
    destinations: ["mixpanel", "hotjar", "sentry"],
    owner: "client"
  },
  password_reset_completed: {
    required: [],
    destinations: ["mixpanel", "hotjar", "sentry"],
    owner: "client"
  },

  editor_opened: {
    required: ["source"],
    destinations: ["mixpanel", "hotjar"],
    owner: "client"
  },

  // modals: shown / dismissed / converted come from AntModal's `trackingName`
  modal_shown: {
    required: ["modal", "trigger"],
    destinations: ["mixpanel", "hotjar", "sentry"],
    owner: "client"
  },
  modal_dismissed: {
    required: ["modal", "how"],
    destinations: ["mixpanel", "hotjar", "sentry"],
    owner: "client"
  },
  modal_converted: {
    required: ["modal", "action"],
    destinations: ["mixpanel", "hotjar", "sentry"],
    owner: "client"
  },
  subscription_cancel_reason_selected: {
    required: ["reason", "retention_offer_shown"],
    destinations: ["mixpanel", "hotjar", "sentry"],
    owner: "client"
  },
  subscription_cancelled: {
    required: ["reason", "level", "interval"],
    destinations: ["mixpanel", "hotjar", "sentry"],
    owner: "client"
  },
  subscription_plan_changed: {
    required: ["from_level", "to_level", "from_interval", "to_interval"],
    destinations: ["mixpanel", "hotjar", "sentry"],
    owner: "client"
  },
  subscription_payment_method_updated: {
    required: ["flow", "was_past_due"],
    destinations: ["mixpanel", "hotjar", "sentry"],
    owner: "client"
  }
};

// maps each catalogue name to itself ({ signup_completed: "signup_completed" })
// the JSDoc type lets TypeScript callers see the keys (reduce infers `{}`)
/** @type {{ [K in keyof typeof CATALOGUE]: K }} */
export const EVENTS = Object.keys(CATALOGUE).reduce((acc, name) => {
  acc[name] = name;
  return acc;
}, {});
